import { FormikConfig } from 'formik';
import { memo, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { CreateModal } from '../../../components/Modal/CreateModal';
import { FormikTextArea } from '../../../components/formik/FormTextArea';
import { T, useT } from '../../../translation/src';
import { useOrganisationContactSalesMutation } from '../../../types/graphqlTypes';

const MAX_MESSAGE_LENGTH = 2000;

const useTranslations = () => {
  const contactSales = useT('contact sales', { swc: true });
  const message = useT('message', { swc: true });
  const placeholder = useT('Let us know how we can help, for example with a subscription, additional projects or a question about pricing.');
  const sent = useT('Your message was sent. Sales will get back to you.');
  const failed = useT('Sending failed. Please try again.');
  return { contactSales, message, placeholder, sent, failed };
};

interface FormValues {
  message: string;
}

export interface ContactSalesModalProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
}

// In-app replacement for a mailto link: the message lands as a notification
// with every superadmin, with the sender and organisation attached.
const ContactSalesModal_: React.FC2<ContactSalesModalProps> = ({ onClose, open, organisationId }) => {
  const [contactSales, { loading }] = useOrganisationContactSalesMutation();
  const translations = useTranslations();

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async (values) => {
      const message = values.message.trim();
      if (!message) return;
      try {
        await contactSales({ variables: { organisationId, message } });
        toast.success(translations.sent);
        // The window unmounts its form as it closes: nothing to reset.
        onClose();
      } catch (error) {
        console.error(error);
        toast.error(translations.failed);
      }
    },
    [contactSales, onClose, organisationId, translations.failed, translations.sent],
  );

  const formik: FormikConfig<FormValues> = useMemo(() => ({ initialValues: { message: '' }, onSubmit }), [onSubmit]);

  return (
    <CreateModal title={translations.contactSales} createButtonTitle={<T _str="send" swc />} formik={formik} onClose={onClose} open={open} isSubmitting={loading} showCloseIcon>
      <FormikTextArea name="message" label={translations.message} placeholder={translations.placeholder} inputClassName="min-h-40" maxLength={MAX_MESSAGE_LENGTH} autoFocus />
    </CreateModal>
  );
};

export const ContactSalesModal = memo(ContactSalesModal_);
